/**
 * Staff push subscriptions.
 *
 * Writes go through the Vercel endpoints (push-subscribe, push-unsubscribe,
 * push-test), which verify the session and write `push_subscriptions` with
 * superuser headers; the collection's create/update rules are null. Reads of
 * the caller's own rows use the SDK under the own-row list rule.
 *
 * Field names follow the collection on Railway: keys_p256dh / keys_auth.
 */

import { pb, escapeFilterValue } from '../lib/pocketbase'

export interface PushSubscriptionRow {
  id: string
  user_id: string
  agency_id: string
  endpoint: string
  keys_p256dh: string
  keys_auth: string
  device_name: string | null
  device_type: 'mobile' | 'desktop' | 'tablet' | null
  user_agent: string | null
  is_active: boolean
  last_used: string | null
  created: string
}

export interface PushTestResult {
  devices: number
  sent: number
  failed: { device: string | null; status: number; message: string }[]
  deactivated: number
  /** Which side of the VAPID pair is wrong when the push service returns 403. */
  keys: {
    /** VAPID_PUBLIC_KEY on the server is the public half of VAPID_PRIVATE_KEY. */
    server_pair_matches: boolean
    /** This browser subscribed with the server's public key (null when the browser did not report one). */
    browser_matches_server: boolean | null
  }
}

export const PUSH_ENDPOINT_KEY = 'reppr_staff_push_endpoint'

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pb.authStore.token}` },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({})) as T & { error?: string }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`)
  return data
}

/** Register the browser's subscription for the signed-in user. */
export async function saveStaffSubscription(subscription: PushSubscription): Promise<PushSubscriptionRow> {
  const json = subscription.toJSON()
  const keys = json.keys as { p256dh?: string; auth?: string } | undefined
  if (!json.endpoint || !keys?.p256dh || !keys?.auth) {
    throw new Error('Browser returned an incomplete push subscription')
  }
  const { subscription: row } = await post<{ subscription: PushSubscriptionRow }>('/api/push-subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: keys.p256dh, auth: keys.auth },
    device_name: defaultDeviceName(),
    device_type: detectDeviceType(),
    user_agent: navigator.userAgent,
  })
  localStorage.setItem(PUSH_ENDPOINT_KEY, json.endpoint)
  return row
}

/** Tell the server this endpoint is off. */
export async function deactivateStaffSubscription(endpoint: string): Promise<void> {
  await post('/api/push-unsubscribe', { endpoint })
  if (localStorage.getItem(PUSH_ENDPOINT_KEY) === endpoint) localStorage.removeItem(PUSH_ENDPOINT_KEY)
}

/**
 * This browser stops receiving the signed-in person's notifications: the
 * server row is turned off, then the browser subscription is dropped. Called
 * on sign-out, while the session still exists to authorise it. A browser
 * that never subscribed has nothing to do.
 */
export async function switchOffThisDevice(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = reg ? await reg.pushManager.getSubscription() : null
  const endpoint = sub?.endpoint ?? localStorage.getItem(PUSH_ENDPOINT_KEY)
  if (!endpoint) return
  await deactivateStaffSubscription(endpoint)
  if (sub) await sub.unsubscribe()
  localStorage.removeItem(PUSH_ENDPOINT_KEY)
}

/** Delete one of the caller's own device rows outright. */
export async function deletePushSubscription(id: string): Promise<void> {
  await pb.collection('push_subscriptions').delete(id)
}

/** The caller's active devices, newest registration first. */
export async function getUserSubscriptions(): Promise<PushSubscriptionRow[]> {
  const user = pb.authStore.model
  if (!user) throw new Error('Not signed in')
  const records = await pb.collection('push_subscriptions').getFullList({
    filter: `user_id = "${escapeFilterValue(user.id)}" && is_active = true`,
    sort: '-last_used,-created',
  })
  return records as unknown as PushSubscriptionRow[]
}

/**
 * A real push through the push service to every active device of the caller.
 * `browserKey` is the applicationServerKey this browser subscribed with, so
 * the server can say which side of the VAPID pair is wrong on a 403.
 */
export async function sendTestPush(browserKey: string | null): Promise<PushTestResult> {
  return post<PushTestResult>('/api/push-test', { browser_key: browserKey })
}

/** Base64url of the key the browser's subscription was created with, if exposed. */
export function subscriptionServerKey(sub: PushSubscription): string | null {
  const key = sub.options?.applicationServerKey
  if (!key) return null
  const bytes = new Uint8Array(key)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// ── Device labelling ──────────────────────────────────────────────────────

function defaultDeviceName(): string {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? 'Android phone' : 'Android tablet'
  if (/Mac/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Browser'
}

function detectDeviceType(): 'mobile' | 'desktop' | 'tablet' {
  const ua = navigator.userAgent
  if (/iPad|Android(?!.*Mobile)|Tablet/.test(ua)) return 'tablet'
  if (/iPhone|Android.*Mobile|webOS|iPod|BlackBerry|IEMobile|Opera Mini/.test(ua)) return 'mobile'
  return 'desktop'
}
